import type { Theme } from '../../contract.js';
import { electricLightLight, electricLightDark } from './variants.js';

/**
 * Electric Light family — LIGHT-ONLY (single-mode, commit 6).
 *
 * A white theme: a white #FAFAFA surface with a higher-contrast DARK
 * accent (#2A2A2F) and dark-grey glow. Its single mode is Light; the
 * near-white #FAFAFA-accent "airier" design that used to be the light
 * variant was dropped (it read as white-on-white on the CTA). See
 * variants.ts — one token body backs both slots, and the dark slot is
 * unreachable under availableModes:['light'].
 *
 * Routes the ChargingAnimation to the orb shader (electric-light variant).
 */
export const electricLightTheme: Theme = {
  id: 'electric-light',
  name: 'Electric Light',
  description: 'Bright pale chrome with white-glow motion.',
  // Greyscale rework swatches (post-C11). The picker's per-row
  // SwatchRow reads variant.tokens.colors.{accent,charging,surface}
  // directly and ignores this top-level array; it's the catalogue/
  // seed-display swatch (see packages/ui DriverThemePicker). Updated to
  // match the new identity so seed output stays in sync.
  swatches: ['#FFFFFF', '#A0A0A0', '#1A1A20'],
  variants: { light: electricLightLight, dark: electricLightDark },
  unlockedByDefault: true,
  costPoints: 0,
  // Light-only family (single-mode infra, commit 4). availableModes clamps
  // the provider to variants['light'] — which holds the dark-accent-on-white
  // design (see variants.ts) — and ModeSegment shows only the Light button.
  // The dark slot is unreachable.
  availableModes: ['light'],
  // Post-greyscale-rework map style decision — both modes paint the
  // Mapbox Light basemap. Previous default sent dark mode to
  // satellite-streets for "consistency with other shader families";
  // Warren's product call after the greyscale rework is that the
  // family identity is "always bright cartographic," and the dark
  // variant's surface palette is dark enough that Mapbox Light reads
  // as a contrasting paper-map basemap rather than a clash.
  mapStyle: {
    light: 'mapbox://styles/mapbox/light-v11',
    dark: 'mapbox://styles/mapbox/light-v11',
  },
  // Theme charging sims v2: Electric Light's active-charging animation is
  // the REAL fluid sim in its marbling personality — graphite/chrome inks
  // on white paper with barely-fading trails. While charging, ink sources
  // sweep across the surface leaving combed marble veins; the finger drags
  // ink exactly like the marbling reference; left alone the paper slowly
  // returns to calm. Light-only family, so the single variant needs no
  // dispatcher mode resolution.
  chargingAnimation: 'shader',
  shaderKind: 'fluid',
  shaderVariant: 'fluid-marbling-light',
};
